import { requireService } from '@repo/di-ez';
import { GRID_ROW_NUMBER_COL_DEF, TableCell, TableInput, TableNumberInput, TableSelect } from '@repo/go-table';
import type { GridColDef, GridRenderCellParams } from '@repo/go-table';
import { hasValue } from '@repo/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTrajStationTypeTKey, TRAJ_STATION_TYPES } from '../../../consts/traj-station-type';
import type { FactTrajectoryPointEntity } from '../../../service/entities';
import { FACT_TRAJECTORY_POINT_KEYS, type TFactTrajectoryPoint } from '../../../service/model';
import { isTrajectoryEditableField } from '../editable-fields';
import { getTrajectoryColumnHeaderTKey } from '../utils/get-column-header-t-key';

const TRAJECTORY_DATA_FIELDS = FACT_TRAJECTORY_POINT_KEYS.filter(
  (field): field is Exclude<(typeof FACT_TRAJECTORY_POINT_KEYS)[number], 'stationId'> => field !== 'stationId',
);

export const useTrajectoryColumns = (useShortColumnHeaders = true, isEditing = false): GridColDef[] => {
  const { t } = useTranslation();
  const trajectoryService = requireService('trajectoryService');

  const typeTrajStationOptions = useMemo(
    () =>
      TRAJ_STATION_TYPES.map((value) => ({
        value,
        label: t(getTrajStationTypeTKey(value)),
      })),
    [t],
  );

  return useMemo((): GridColDef[] => {
    const baseColumn: Partial<GridColDef<FactTrajectoryPointEntity>> = {
      headerAlign: 'left',
      minWidth: 100,
      resizable: true,
      disableColumnMenu: isEditing,
      disableReorder: isEditing,
      sortable: !isEditing,
      valueGetter: (_, row, column) => {
        return trajectoryService.getFieldValue(row?.entityId, column.field as keyof TFactTrajectoryPoint);
      },
    };

    const dataColumns: GridColDef<FactTrajectoryPointEntity>[] = TRAJECTORY_DATA_FIELDS.map((field) => {
      const isEditable = isEditing && isTrajectoryEditableField(field);
      const headerName = t(getTrajectoryColumnHeaderTKey(field, useShortColumnHeaders));

      switch (field) {
        case 'typeTrajStation':
          return {
            ...baseColumn,
            field,
            type: 'singleSelect' as const,
            minWidth: 220,
            editable: isEditable,
            headerName,
            valueOptions: typeTrajStationOptions,
            renderCell: (params: GridRenderCellParams<FactTrajectoryPointEntity>) => {
              const typeTrajStation = trajectoryService.getFieldValue(params.row.entityId, 'typeTrajStation');
              const label = hasValue(typeTrajStation)
                ? t(getTrajStationTypeTKey(typeTrajStation), { defaultValue: typeTrajStation })
                : '—';

              return <TableCell>{label}</TableCell>;
            },
            renderEditCell: (params: GridRenderCellParams<FactTrajectoryPointEntity>) => (
              <TableCell>
                <TableSelect
                  autoFocus
                  defaultOpen
                  cellParams={params}
                  value={trajectoryService.getFieldValue(params.row.entityId, 'typeTrajStation')}
                  options={typeTrajStationOptions}
                  onChange={(value) => {
                    if (typeof value !== 'string') {
                      return;
                    }

                    trajectoryService.updateMainPointField(params.row.entityId, { typeTrajStation: value });
                  }}
                />
              </TableCell>
            ),
          };
        case 'comment':
          return {
            ...baseColumn,
            field,
            type: 'string' as const,
            editable: isEditable,
            headerName,
            renderEditCell: (params: GridRenderCellParams<FactTrajectoryPointEntity>) => (
              <TableInput
                cellParams={params}
                value={trajectoryService.getFieldValue(params.row.entityId, 'comment')}
                onChange={({ target: { value } }) => {
                  trajectoryService.updateMainPointField(params.row.entityId, { comment: value || null });
                }}
              />
            ),
          };
        default:
          return {
            ...baseColumn,
            field,
            type: 'number' as const,
            editable: isEditable,
            headerName,
            renderEditCell: (params: GridRenderCellParams<FactTrajectoryPointEntity, number>) => (
              <TableNumberInput
                cellParams={params}
                allowClear={false}
                autoFocus
                value={trajectoryService.getFieldValue(params.row.entityId, field)}
                onChange={(value) => {
                  if (typeof value !== 'number' && hasValue(value)) {
                    return;
                  }

                  trajectoryService.updateMainPointField(params.row.entityId, { [field]: value ?? null });
                }}
              />
            ),
          };
      }
    });

    return [
      {
        ...GRID_ROW_NUMBER_COL_DEF,
      },
      ...dataColumns,
    ];
  }, [isEditing, t, trajectoryService, typeTrajStationOptions, useShortColumnHeaders]);
};
